import apiClient from './api';

export type AudioToVideoStatus = 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
export type AudioToVideoStepId = 'prepareAudio' | 'prepareImage' | 'renderBase' | 'burnCaptions' | 'finalize';
export type AspectRatio = '16:9' | '9:16' | '1:1' | '4:5';

// Kept in sync with AudioToVideoService.CAPTION_FONTS on the backend —
// these are the only font families actually installed in the rendering
// container (see backend/Dockerfile), so the list is a fixed allow-list,
// not free text.
export const CAPTION_FONT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'auto', label: 'Auto-detect from transcript (recommended)' },
  { value: 'Noto Sans', label: 'Noto Sans — Latin/English' },
  { value: 'Noto Sans Tamil', label: 'Noto Sans Tamil' },
  { value: 'Noto Sans Devanagari', label: 'Noto Sans Devanagari — Hindi, Marathi' },
  { value: 'Noto Sans Telugu', label: 'Noto Sans Telugu' },
  { value: 'Noto Sans Kannada', label: 'Noto Sans Kannada' },
  { value: 'Noto Sans Malayalam', label: 'Noto Sans Malayalam' },
  { value: 'Noto Sans Bengali', label: 'Noto Sans Bengali' },
  { value: 'Noto Sans Gujarati', label: 'Noto Sans Gujarati' },
  { value: 'Noto Sans Gurmukhi', label: 'Noto Sans Gurmukhi — Punjabi' },
  { value: 'Noto Sans Oriya', label: 'Noto Sans Oriya' },
  { value: 'Noto Sans Arabic', label: 'Noto Sans Arabic — Urdu' },
];

export interface AudioToVideoStep {
  id: AudioToVideoStepId;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped';
  startedAt?: string;
  completedAt?: string;
  error?: string;
}

export interface AudioToVideoJob {
  jobId: string;
  title?: string;
  titleOverlay?: { enabled: boolean };
  audioSource: {
    sourceType: 'ttsJob' | 'translationJob' | 'upload';
    ttsJobId?: string;
    translationJobId?: string;
    videoUploadFileId?: string;
    duration?: number;
  };
  transcriptText?: string;
  backgroundImage: { backgroundImageId: string };
  captions: { enabled: boolean; position?: 'bottom' | 'top'; fontFamily?: string };
  aspectRatio: AspectRatio;
  status: AudioToVideoStatus;
  progress: number;
  currentStep: string;
  error?: string;
  steps: AudioToVideoStep[];
  result?: {
    videoS3Key: string;
    duration: number;
    width: number;
    height: number;
    size: number;
    format: string;
    videoUploadFileId?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateAudioToVideoRequest {
  title?: string;
  titleOverlay?: { enabled: boolean };
  audioSource: {
    sourceType: 'ttsJob' | 'translationJob' | 'upload';
    ttsJobId?: string;
    translationJobId?: string;
    videoUploadFileId?: string;
  };
  transcriptText?: string;
  backgroundImageId: string;
  captions: { enabled: boolean; position?: 'bottom' | 'top'; fontFamily?: string };
  aspectRatio: AspectRatio;
}

const BASE = '/audio-to-video';

export const audioToVideoApi = {
  createJob: async (data: CreateAudioToVideoRequest): Promise<{ jobId: string; job: AudioToVideoJob }> => {
    const res = await apiClient.post(BASE, data);
    return res.data;
  },

  getJobs: async (params?: { ttsJobId?: string }): Promise<{ jobs: AudioToVideoJob[] }> => {
    const res = await apiClient.get(BASE, { params });
    return res.data;
  },

  getJob: async (jobId: string): Promise<{ job: AudioToVideoJob }> => {
    const res = await apiClient.get(`${BASE}/${jobId}`);
    return res.data;
  },

  retryJob: async (jobId: string): Promise<{ success: boolean; message: string }> => {
    const res = await apiClient.post(`${BASE}/${jobId}/retry`);
    return res.data;
  },

  cancelJob: async (jobId: string): Promise<{ success: boolean; message: string }> => {
    const res = await apiClient.delete(`${BASE}/${jobId}`);
    return res.data;
  },

  getVideoUrl: async (jobId: string): Promise<{ url: string }> => {
    const res = await apiClient.get(`${BASE}/${jobId}/video-url`);
    return res.data;
  },

  getThumbnailUrl: async (jobId: string): Promise<{ url: string }> => {
    const res = await apiClient.get(`${BASE}/${jobId}/thumbnail-url`);
    return res.data;
  },
};
